import { useQuery } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { useMemo } from "react";
import { DataProviderEnum } from "../../../../../apiClient/generated";
import {
    useAppSelector,
    useEmissionRecordsApiClient,
    useInfrastructureApiClient,
} from "../../../../../hooks";
import { emissionPageStateFamily, selectionContextAtom } from "../../state";
import { TimelineBlock } from "./base";
import { TimelineHeader } from "./Header";
import { RollingAveragesTimelineBlock } from "./RollingAverages";
import { SatelliteEmissions } from "./Satellite";
import { SensirionDataTimelineBlock } from "./Sensirion";

interface TimelineProps {
    siteId: string;
}

export const Timeline = (props: TimelineProps) => {
    const userFlags = useAppSelector((s) => s.auth.flags);
    const { dateFrom, dateTo, emissionFilters } = useAtomValue(
        emissionPageStateFamily(props.siteId),
    );
    const [contextSelection] = useAtom(selectionContextAtom);

    // Site info
    const apiClient = useInfrastructureApiClient();
    const siteQuery = useQuery({
        queryKey: ["site", props.siteId],
        queryFn: async () => {
            const response = await apiClient.infrastructureSitesRetrieve({
                id: props.siteId,
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    const dateRange = useMemo(
        () => ({
            start: dateFrom,
            end: dateTo,
        }),
        [dateFrom, dateTo],
    );

    const emissionsApiClient = useEmissionRecordsApiClient();
    const emissionPreviews = useQuery({
        queryKey: [
            "datesWithEmissions",
            props.siteId,
            dateFrom,
            dateTo,
            emissionFilters,
        ],
        queryFn: async () => {
            const response =
                await emissionsApiClient.emissionRecordsEmissionCountsList({
                    ...emissionFilters,
                    relatedSite: props.siteId,
                    detectionDateRangeAfter: dateFrom,
                    detectionDateRangeBefore: dateTo,
                    providerWithSource: emissionFilters.providerWithSource
                        ? (JSON.stringify(
                              emissionFilters.providerWithSource,
                          ) as any)
                        : undefined,
                });
            return response.map((i) => i.date);
        },
    });

    return (
        <div className="text-xs flex-1">
            {/* Header! -> date range + timeline with brushes */}
            <TimelineHeader dataDateRange={dateRange} />

            {/* List of sources + plots */}
            <TimelineBlock
                title="Satellite/Aerial Surveys"
                initiallyOpen
                loading={emissionPreviews.isLoading}
                previewPoints={emissionPreviews.data || []}
                dataDateRange={
                    contextSelection.dateRange
                        ? contextSelection.dateRange
                        : dateRange
                }
            >
                <SatelliteEmissions siteId={props.siteId} />
            </TimelineBlock>

            {/* Sensirion data */}
            {siteQuery.data?.sensorConfig?.sensirion_site_id && (
                <SensirionDataTimelineBlock
                    siteId={props.siteId}
                    sensirionSiteId={
                        siteQuery.data?.sensorConfig?.sensirion_site_id
                    }
                    dataProvider={DataProviderEnum.Sensirion}
                />
            )}

            {/* LongPath data */}
            {siteQuery.data?.sensorConfig?.longpath_site_id &&
                userFlags.includes("enable_longpath_data") && (
                    <SensirionDataTimelineBlock
                        siteId={props.siteId}
                        sensirionSiteId={
                            siteQuery.data?.sensorConfig?.longpath_site_id
                        }
                        dataProvider={DataProviderEnum.Longpath}
                    />
                )}

            {/* LongPath Rolling Averages */}
            {siteQuery.data?.sensorConfig?.longpath_site_id &&
                userFlags.includes("enable_longpath_data") && (
                    <RollingAveragesTimelineBlock siteId={props.siteId} />
                )}
        </div>
    );
};
